import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { FormComponent } from 'app/common/components/form/form.component';
import { GlobalsService } from 'app/common/services/globals.service';
import { ConferenceRegistrationService } from 'app/conference-registration/conference-registration.service';
import { ConventionRegistrationService } from 'app/convention-registration/convention-registration.service';
import { ConferenceRegistration } from 'app/models/conference-registration.model';
import { ConventionRegistration } from 'app/models/convention-registration.model';
import { Lookup, MRMValidators, PersistData, ProgressBarService, WizardTab, WizardTabComponent } from '@mrm/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'contact-info-form',
  templateUrl: './contact-info-form.component.html'
})
export class ContactInfoFormComponent extends FormComponent implements OnInit, OnDestroy {
  @Input() isWizardTab: boolean = true;
  @Input() contactInfoProps = {
    emailDescription: '',
    confirmEmailDescription: '',
    adminEmailDescription: '',
    confirmAdminEmailDescription: '',
    emailTypeDescription: '',
    showCellPhone: true,
    showAdminEmail: false,
    showDefaultContactEmail: false, // For Conventions (for those that are Virtual)
    showDefaultContactAsAdminEmail: false, // used to store admin email but disaplayed as default contact email
    isVirtual: false, // Identifies whether the Conference/Convention is hosted virtually (show different labels, fields).
    isObserver: false, // Used for Observer registrations for Virtual Conventions
    conferenceDBID: null
  };
  @Input() set addressTypeValues(value: Lookup[]) {
    this.addressTypes = value;
  }

  @Output() addressTypeChanged = new EventEmitter();
  @Output() provinceChanged = new EventEmitter();

  provinces: Lookup[] = [];
  addressTypes: Lookup[];
  emailTypes: Lookup[];

  constructor(
    progressBarService: ProgressBarService,
    toastr: ToastrService,
    translateService: TranslateService,
    router: Router,
    modalService: NgbModal,
    private _globalsService: GlobalsService,
    private _persistData: PersistData,
    private _conventionRegistrationService: ConventionRegistrationService,
    private _conferenceRegistrationService: ConferenceRegistrationService,
    private _wizardTabComponent: WizardTabComponent
  ) {
    super(progressBarService, toastr, translateService, router, modalService);
  }

  get address(): FormGroup {
    return <FormGroup>this.form.get('address');
  }

  get workPhone(): FormGroup {
    return <FormGroup>this.form.get('workPhone');
  }

  get homePhone(): FormGroup {
    return <FormGroup>this.form.get('homePhone');
  }

  get cellPhone(): FormGroup {
    return <FormGroup>this.form.get('cellPhone');
  }

  get faxPhone(): FormGroup {
    return <FormGroup>this.form.get('faxPhone');
  }

  get emailAddress(): FormGroup {
    return <FormGroup>this.form.get('emailAddress');
  }

  get administrativeEmailAddress(): FormGroup {
    return <FormGroup>this.form.get('administrativeEmailAddress');
  }

  get hasConfirmEmail(): boolean {
    return this.emailAddress.controls['confirmValue'] !== undefined;
  }

  get hasAdminEmail(): boolean {
    return this.administrativeEmailAddress && this.administrativeEmailAddress.controls['confirmValue'] !== undefined;
  }

  ngOnDestroy(): void {

  }

  ngOnInit(): void {
    this._initLookups();

    if (this.isWizardTab) {
      const tab: WizardTab = new WizardTab();
      tab.selected = false;

      if (!this.contactInfoProps.isObserver) {
        this.form.get('workPhone').get('value').setValidators([Validators.required, MRMValidators.phoneNumber]);
        this.form.get('address').get('postalCode').setValidators([Validators.required, MRMValidators.postalCode]);
        this.form.get('homePhone').get('value').clearValidators();
        this.form.get('cellPhone').get('value').clearValidators();
        this.form.get('faxPhone').get('value').clearValidators();
        this.form.get('homePhone').get('value').setValidators([MRMValidators.phoneNumber]);
        this.form.get('cellPhone').get('value').setValidators([MRMValidators.phoneNumber]);
        this.form.get('faxPhone').get('value').setValidators([MRMValidators.phoneNumber]);
      }
      tab.formGroup = this.form;

      this._wizardTabComponent.addTab(tab);
    }
  }

  onAddressTypeChanged(): void {
    this.addressTypeChanged.emit();
  }

  onProvinceChange(): void {
    this.provinceChanged.emit();
  }

  onPasteEmail(e: Event): void {
    e.preventDefault();
  }

  onUseAdminChanged(): void {
    const useAdmin = this.form.get('administrativeEmailAddress').get('useAdminEmail').value;
    this.contactInfoProps.showAdminEmail = useAdmin;

    if (useAdmin) {
      this.form.get('administrativeEmailAddress').get('value').setValidators([Validators.required, MRMValidators.invalidEmail]);
      this.form.get('administrativeEmailAddress').get('value').updateValueAndValidity();
      this.form.get('administrativeEmailAddress').get('confirmValue').setValidators([Validators.required, MRMValidators.invalidEmail]);
      this.form.get('administrativeEmailAddress').get('confirmValue').updateValueAndValidity();
    } else {
      this.form.get('administrativeEmailAddress').get('value').clearValidators();
      this.form.get('administrativeEmailAddress').get('value').updateValueAndValidity();
      this.form.get('administrativeEmailAddress').get('confirmValue').clearValidators();
      this.form.get('administrativeEmailAddress').get('confirmValue').updateValueAndValidity();
    }
  }

  onConfirmEmailChanged(): void {
    // confirm email is unique.
    const email = this.form.get('emailAddress').get('value').value;
    if (email != null
      && email.length > 0
      && email == this.form.get('emailAddress').get('confirmValue').value) {

      const conventionRegistration: ConventionRegistration = this._persistData.get('conventionRegistration');

      if (conventionRegistration != null) {
        this._progressBarService.start();
        this._conventionRegistrationService.isUniqueEmail(conventionRegistration.conventionDBID, email)
          .subscribe(this._onCheckUniqueEmailSuccess.bind(this), this.displayError.bind(this));
      } else {
        this._progressBarService.start();
        this._conferenceRegistrationService.isUniqueEmail(this.contactInfoProps.conferenceDBID, email)
          .subscribe(this._onCheckUniqueEmailSuccess.bind(this), this.displayError.bind(this));
      }
    }
  }

  private _onCheckUniqueEmailSuccess(isUnique: boolean) {
    // If we have an error, it's not unique so we would not get to this method so no need to do anything but stop progress bar.
    this._progressBarService.stop();
  }

  private _initLookups() {
    this.provinces = this._globalsService.provinces;
    this.addressTypes = this._globalsService.addressTypes;
    this.emailTypes = this._globalsService.electronicAddressTypes.filter(x => x.code != '3');
  }
}